import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { getApplicationByRef, checkSlug as apiCheckSlug, createTenant, listGenres } from '../services/api';
import type { GenreGroup } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { buildTenantUrl } from '../hooks/useTenant';
import TermsAcceptanceModal from '../components/TermsAcceptanceModal';
import { buildTenantPath } from '../hooks/useTenant';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 }
  }
};

interface FormData {
  ownerName: string;
  ownerEmail: string;
  artistName: string;
  slug: string;
  primaryGenreId: number | null;
}

export default function ArtistEnrolmentPage({ artistReference }: { artistReference: string }) {
  const { login } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [appData, setAppData] = useState<{ artistName: string; email: string } | null>(null);
  const [notFound, setNotFound] = useState(false);

  const [step, setStep] = useState<'form' | 'success'>('form');
  const [form, setForm] = useState<FormData>({
    ownerName: '',
    ownerEmail: '',
    artistName: '',
    slug: '',
    primaryGenreId: null,
  });
  const [genreGroups, setGenreGroups] = useState<GenreGroup[]>([]);
  const [genresLoading, setGenresLoading] = useState(true);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [slugChecking, setSlugChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Load genre groups
  useEffect(() => {
    (async () => {
      try {
        const res = await listGenres() as unknown as Response;
        const data = await res.json() as { data: GenreGroup[] };
        if (data.data) setGenreGroups(data.data);
      } catch {
        // Genres are optional; fail silently
      } finally {
        setGenresLoading(false);
      }
    })();
  }, []);

  // Load application data
  useEffect(() => {
    (async () => {
      try {
        const res = await getApplicationByRef(artistReference);
        const data = await res.json() as any;
        if (res.ok && data.data) {
          setAppData(data.data);
          setForm(prev => ({
            ...prev,
            ownerName: '',
            ownerEmail: data.data.email,
            artistName: data.data.artistName,
          }));
          // Auto-generate slug from artist name
          const slug = data.data.artistName
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '')
            .slice(0, 30);
          setForm(prev => ({ ...prev, slug }));
          if (slug.length >= 3) {
            checkSlug(slug);
          }
        } else {
          setNotFound(true);
        }
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [artistReference]);

  // Check slug availability
  const checkSlug = useCallback(async (slug: string) => {
    if (!slug || slug.length < 3) {
      setSlugAvailable(null);
      return;
    }

    setSlugChecking(true);
    try {
      const res = await apiCheckSlug(slug);
      const data = await res.json() as { available: boolean };
      setSlugAvailable(data.available);
    } catch {
      setSlugAvailable(null);
    } finally {
      setSlugChecking(false);
    }
  }, []);

  const handleSlugChange = (value: string) => {
    const cleaned = value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 30);
    setForm(prev => ({ ...prev, slug: cleaned }));
    if (cleaned.length >= 3) {
      checkSlug(cleaned);
    } else {
      setSlugAvailable(null);
    }
  };

  const genreSelectionRequired = genreGroups.length > 0;
  const hasRequiredFields =
    form.ownerName.trim().length > 0 &&
    form.ownerEmail.trim().length > 0 &&
    form.artistName.trim().length > 0 &&
    form.slug.trim().length >= 3;

  // Show terms modal when form is submitted
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (slugChecking) {
      return;
    }

    if (!hasRequiredFields) {
      setError('Please complete all required fields.');
      return;
    }

    if (genreSelectionRequired && form.primaryGenreId === null) {
      setError('Please choose a primary genre.');
      return;
    }

    // Final availability check at submit-time to prevent false negatives from
    // transient client state.
    let available = slugAvailable;
    if (available !== true) {
      setSlugChecking(true);
      try {
        const res = await apiCheckSlug(form.slug);
        const data = await res.json() as { available: boolean };
        available = data.available;
        setSlugAvailable(data.available);
      } catch {
        available = null;
      } finally {
        setSlugChecking(false);
      }
    }

    if (available !== true) {
      setError('This subdomain is unavailable or could not be verified. Please try a different subdomain.');
      return;
    }

    setShowTermsModal(true);
  };

  // Actually create the tenant after terms acceptance
  const handleTermsAccepted = async () => {
    setSubmitting(true);

    try {
      const { primaryGenreId, ...rest } = form;
      const res = await createTenant({ ...rest, ...(primaryGenreId ? { primaryGenreId } : {}), termsAccepted: true });

      const data = await res.json() as any;

      if (!res.ok) {
        setError(data.error || 'Something went wrong');
        setShowTermsModal(false);
        setSubmitting(false);
        return;
      }

      // Login with the returned token
      if (data.token) {
        login({
          id: data.userId,
          email: form.ownerEmail,
          name: form.ownerName,
          role: 'artist',
          tenantId: data.data.id,
          tenant: { id: data.data.id, slug: form.slug, artistName: form.artistName },
        }, data.token);
      }

      setShowTermsModal(false);
      setStep('success');
    } catch (err: any) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const isValid =
    hasRequiredFields &&
    slugAvailable !== false &&
    (!genreSelectionRequired || form.primaryGenreId !== null) &&
    !slugChecking;

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-electric-cyan animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center px-4">
        <div className="glass-card p-8 sm:p-10 max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/10 flex items-center justify-center">
            <XCircle className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold mb-3">Invalid Link</h2>
          <p className="text-text-secondary mb-6">
            This enrolment link is invalid or has not been approved yet.
          </p>
          <a href="/" className="btn-secondary inline-flex items-center">
            Back to Home
          </a>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card p-8 sm:p-10 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-electric-cyan/10 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-electric-cyan" />
          </div>
          <h2 className="text-2xl font-bold mb-3">Terminal Launched</h2>
          <p className="text-text-secondary mb-6">
            Your sovereign domain is live at:
          </p>
          <div className="glass-card p-4 mb-6">
            <code className="font-mono text-electric-cyan text-lg">
              {buildTenantUrl(form.slug)}
            </code>
          </div>
          <p className="text-sm text-text-muted mb-6">
            Welcome to the sovereign artist network, {form.artistName}.
          </p>
          <a
            href={buildTenantPath(form.slug)}
            className="btn-primary inline-flex items-center"
          >
            Go to Dashboard
            <ArrowRight className="w-4 h-4 ml-2" />
          </a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-charcoal flex items-center justify-center px-4 py-16">
      {/* Background */}
      <div className="absolute inset-0 waveform-bg opacity-30" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-electric-cyan/5 rounded-full blur-[120px]" />

      <motion.div
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
        className="relative z-10 w-full max-w-lg"
      >
        {/* Header */}
        <motion.div variants={fadeInUp} className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src="/fans-audio-logo.png" alt="fans.audio logo" className="w-8 h-8 object-contain" />
            <span className="font-mono font-bold text-lg">fans<span className="text-electric-cyan">.audio</span></span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Complete Artist Profile</h1>
          <p className="text-text-secondary text-sm">
            Claim your sovereign subdomain and launch your terminal.
          </p>
        </motion.div>

        {/* Form */}
        <motion.form variants={fadeInUp} onSubmit={handleSubmit} className="glass-card p-6 sm:p-8 space-y-5">
          {/* Name */}
          <div>
            <label className="terminal-label block mb-2">your_name</label>
            <input
              type="text"
              value={form.ownerName}
              onChange={(e) => setForm(prev => ({ ...prev, ownerName: e.target.value }))}
              placeholder="Your full name"
              className="w-full px-4 py-3 bg-surface border border-white/10 rounded-lg text-white placeholder-text-muted focus:border-electric-cyan/50 focus:outline-none focus:ring-1 focus:ring-electric-cyan/30 transition-colors"
              required
            />
          </div>

          {/* Email (read-only) */}
          <div>
            <label className="terminal-label block mb-2">email</label>
            <input
              type="email"
              value={form.ownerEmail}
              readOnly
              className="w-full px-4 py-3 bg-surface/50 border border-white/5 rounded-lg text-text-secondary cursor-not-allowed"
            />
          </div>

          {/* Artist/Band Name (read-only) */}
          <div>
            <label className="terminal-label block mb-2">artist_name</label>
            <input
              type="text"
              value={form.artistName}
              readOnly
              className="w-full px-4 py-3 bg-surface/50 border border-white/5 rounded-lg text-text-secondary cursor-not-allowed"
            />
          </div>

          {/* Tenant ID / Subdomain */}
          <div>
            <label className="terminal-label block mb-2">subdomain</label>
            <div className="flex items-center gap-2">
              <div className="relative w-44 sm:w-52">
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  placeholder="my-band"
                  className="w-full px-4 py-3 bg-surface border border-white/10 rounded-lg text-white placeholder-text-muted focus:border-electric-cyan/50 focus:outline-none focus:ring-1 focus:ring-electric-cyan/30 transition-colors pr-10 font-mono"
                  required
                  minLength={3}
                  maxLength={30}
                />
                {/* Status indicator */}
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {slugChecking && <Loader2 className="w-4 h-4 text-text-muted animate-spin" />}
                  {!slugChecking && slugAvailable === true && <CheckCircle className="w-4 h-4 text-green-400" />}
                  {!slugChecking && slugAvailable === false && <XCircle className="w-4 h-4 text-red-400" />}
                </div>
              </div>
              <span className="font-mono text-sm text-text-secondary">.fans.audio</span>
            </div>
            {/* Availability indicator */}
            {form.slug && slugAvailable === false && (
              <p className="mt-2 text-xs font-mono">
                <span className="text-red-400">This subdomain is already taken</span>
              </p>
            )}
            {form.slug && slugAvailable !== false && (
              <p className="mt-2 text-xs text-text-muted">
                Your public URL will be <span className="font-mono text-electric-cyan">{form.slug}.fans.audio</span>
              </p>
            )}
          </div>

          {/* Primary Genre */}
          <div>
            <label className="terminal-label block mb-2">primary_genre</label>
            <p className="text-text-secondary text-xs mb-3 leading-relaxed">
              Choose a primary genre for discovery and distribution. Don&apos;t worry about being &lsquo;boxed in&rsquo;&mdash;you can add &lsquo;Genre Fluid&rsquo; tags and sub-genres once your profile is live.
            </p>
            {genresLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 text-text-muted animate-spin" />
              </div>
            ) : (
              <div className="max-h-60 overflow-y-auto rounded-lg border border-white/10 bg-surface divide-y divide-white/5 custom-scrollbar">
                {genreGroups.map((group) => (
                  <div key={group.id}>
                    {/* Group header — not selectable */}
                    <div className="px-4 py-2 bg-surface-dark sticky top-0 z-10">
                      <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{group.name}</span>
                    </div>
                    {/* Genre options */}
                    <div className="flex flex-wrap gap-2 px-4 py-3">
                      {group.genres.map((genre) => {
                        const isSelected = form.primaryGenreId === genre.id;
                        return (
                          <button
                            key={genre.id}
                            type="button"
                            onClick={() => setForm(prev => ({ ...prev, primaryGenreId: genre.id }))}
                            className={`px-3 py-1.5 text-sm rounded-full border transition-all duration-200 cursor-pointer ${
                              isSelected
                                ? 'bg-electric-cyan/15 border-electric-cyan/60 text-electric-cyan shadow-[0_0_8px_rgba(0,245,255,0.15)]'
                                : 'bg-surface-light/50 border-white/10 text-text-secondary hover:border-white/25 hover:text-white'
                            }`}
                          >
                            {genre.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={!isValid || submitting}
            className="btn-primary w-full !justify-center disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-none"
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                Create Profile
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </button>
        </motion.form>

        {/* Footer */}
        <motion.p variants={fadeInUp} className="text-center text-xs text-text-muted mt-6 font-mono">
          &gt; no credit card. no subscriber tax. just sovereignty.
        </motion.p>
      </motion.div>

      {/* Terms Acceptance Modal */}
      <TermsAcceptanceModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={handleTermsAccepted}
        submitting={submitting}
      />
    </div>
  );
}
